import { z } from 'zod';

import { IdSchema, IsoDateSchema } from './common.js';
import { MealTypeEnum } from './diet.js';
import { FoodIdSchema, FoodSizeLabelEnum } from './foods.js';

// ─── Day-keying primitives ────────────────────────────────────────────────

// YYYY-MM-DD computed server-side at write time from the client-supplied
// timezone. See ADR-0013 + the localDate field in apps/api/prisma/schema.prisma.
const LOCAL_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
export const LocalDateSchema = z.string().regex(LOCAL_DATE_PATTERN, {
  message: 'localDate must be YYYY-MM-DD',
});
export type LocalDate = z.infer<typeof LocalDateSchema>;

// IANA timezone string. Validated loosely at the schema layer; the API layer
// will round-trip through Intl.DateTimeFormat to confirm the timezone resolves
// before persisting. Loose here keeps shared dependency-free.
export const TimezoneSchema = z.string().min(1).max(64);
export type Timezone = z.infer<typeof TimezoneSchema>;

// ─── NutritionLog enums ───────────────────────────────────────────────────

// Provenance of a logged entry. `manual` is the default user-typed entry.
// `diet_plan_copy` flags rows created by the "ate the diet plan as-is"
// shortcut (one tap converts the active diet plan's slots into log rows).
// `quick_repeat` is reserved for a future "same as yesterday" shortcut;
// landed now so the schema doesn't need a backfill when that ships.
export const NutritionLogSourceEnum = z.enum(['manual', 'diet_plan_copy', 'quick_repeat']);
export type NutritionLogSource = z.infer<typeof NutritionLogSourceEnum>;

// ─── NutritionLog row shape (matches Prisma model) ────────────────────────

// Mirrors the Prisma model field-for-field with dates as ISO strings. Macros
// are the denormalized snapshot computed server-side at write time from
// Food + quantityG; clients receive them but never set them. See ADR-0013.
export const NutritionLogSchema = z.object({
  id: IdSchema,
  userId: IdSchema,
  foodId: FoodIdSchema,
  quantityG: z.number().positive().max(2000),
  kcal: z.number().min(0).max(20000),
  proteinG: z.number().min(0).max(2000),
  carbsG: z.number().min(0).max(2000),
  fatG: z.number().min(0).max(2000),
  loggedAt: IsoDateSchema,
  localDate: LocalDateSchema,
  timezone: TimezoneSchema,
  mealType: MealTypeEnum,
  source: NutritionLogSourceEnum,
  planSlotId: IdSchema.nullable(),
  // ADR-0015 display fields. Preserve how the entry was logged so the UI
  // can render "2 medium rotis (60 g)" instead of just "60 g rotis".
  // quantityG remains canonical for macro math. The "exactly one mode"
  // invariant lives on the Create DTO at the API layer (M5.5 commit 3),
  // not here — read-side rows may legally have all NULL (legacy logs).
  count: z.number().int().positive().nullable(),
  sizeLabel: FoodSizeLabelEnum.nullable(),
  portionLabel: FoodSizeLabelEnum.nullable(),
  createdAt: IsoDateSchema,
  updatedAt: IsoDateSchema,
});
export type NutritionLog = z.infer<typeof NutritionLogSchema>;

// ─── Create DTO ───────────────────────────────────────────────────────────

// What the client POSTs to log a food. Per ADR-0015, the client picks one
// of four input modes:
//   - count:    { foodId, count, sizeLabel? }
//   - portion:  { foodId, portionLabel }
//   - grams:    { foodId, quantityG }                  (legitimate only when
//                                                       food.unitType=grams)
//   - manual:   { foodId, quantityG, manualOverride: true }
// The cross-field superRefine below rejects any combination that picks more
// than one mode (or zero). Service-layer validation (M5.5 commit 3) checks
// the picked mode against food.unitType after findFoodById.
//
// Server computes from Food row + canonical quantityG: kcal, proteinG,
// carbsG, fatG. Server computes from loggedAt + timezone: localDate.
//
// `.strict()` rejects unknown keys so a client cannot smuggle macros or a
// pre-computed localDate in. The server is authoritative for all four.
export const CreateNutritionLogDtoSchema = z
  .object({
    foodId: FoodIdSchema,
    quantityG: z.number().positive().max(2000).optional(),
    mealType: MealTypeEnum,
    timezone: TimezoneSchema,
    loggedAt: IsoDateSchema.optional(),
    source: NutritionLogSourceEnum.optional().default('manual'),
    planSlotId: IdSchema.optional(),
    count: z.number().int().positive().optional(),
    sizeLabel: FoodSizeLabelEnum.optional(),
    portionLabel: FoodSizeLabelEnum.optional(),
    manualOverride: z.boolean().optional(),
  })
  .strict()
  .superRefine(refineNutritionInputMode);
export type CreateNutritionLogDto = z.infer<typeof CreateNutritionLogDtoSchema>;

// Shared input-mode refine. Used by Create (always) and Update (only when at
// least one quantity-affecting field is present in the patch). Counts the
// modes detected on the input and requires exactly 1, plus a couple of
// pre-checks for fields whose meaning depends on another field.
function refineNutritionInputMode(
  dto: {
    quantityG?: number | null;
    count?: number | null;
    sizeLabel?: 'small' | 'medium' | 'large' | null;
    portionLabel?: 'small' | 'medium' | 'large' | null;
    manualOverride?: boolean;
  },
  ctx: z.RefinementCtx,
): void {
  const hasCount = dto.count !== undefined && dto.count !== null;
  const hasPortion = dto.portionLabel !== undefined && dto.portionLabel !== null;
  const hasQuantity = dto.quantityG !== undefined;
  const hasOverride = dto.manualOverride === true;
  const hasSize = dto.sizeLabel !== undefined && dto.sizeLabel !== null;

  if (hasSize && !hasCount) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['sizeLabel'],
      message: "sizeLabel is only valid in count mode (provide 'count' as well)",
    });
    return;
  }

  if (hasOverride && !hasQuantity) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['manualOverride'],
      message: 'manualOverride=true requires quantityG',
    });
    return;
  }

  const modes: string[] = [];
  if (hasCount) modes.push('count');
  if (hasPortion) modes.push('portion');
  if (hasQuantity) modes.push(hasOverride ? 'manualOverride' : 'grams');

  if (modes.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['quantityG'],
      message:
        'exactly one input mode required: count, portionLabel, quantityG, or quantityG+manualOverride',
    });
    return;
  }
  if (modes.length > 1) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['quantityG'],
      message: `multiple input modes detected (${modes.join(', ')}); pick exactly one`,
    });
  }
}

// ─── Update DTO ───────────────────────────────────────────────────────────

// Editable post-write fields only. To swap the food, the client must delete
// and recreate — keeps the snapshot semantics clean (kcal/macros always
// derive from the foodId on the row at write time).
//
// Macros and localDate are NOT editable: server recomputes macros if
// quantityG changes, and recomputes localDate if loggedAt or timezone
// changes. foodId is immutable. source is immutable post-write — once a
// row's provenance is set, the client cannot rewrite it.
export const UpdateNutritionLogDtoSchema = z
  .object({
    quantityG: z.number().positive().max(2000).optional(),
    loggedAt: IsoDateSchema.optional(),
    mealType: MealTypeEnum.optional(),
    timezone: TimezoneSchema.optional(),
    planSlotId: IdSchema.nullable().optional(),
    count: z.number().int().positive().nullable().optional(),
    sizeLabel: FoodSizeLabelEnum.nullable().optional(),
    portionLabel: FoodSizeLabelEnum.nullable().optional(),
    manualOverride: z.boolean().optional(),
  })
  .strict()
  .superRefine((dto, ctx) => {
    // Only run the input-mode refine when the patch actually touches a
    // quantity-affecting field. Patches that only change mealType /
    // loggedAt / timezone / planSlotId pass through unchanged.
    const touchesQuantity =
      dto.quantityG !== undefined ||
      dto.count !== undefined ||
      dto.sizeLabel !== undefined ||
      dto.portionLabel !== undefined ||
      dto.manualOverride !== undefined;
    if (touchesQuantity) {
      refineNutritionInputMode(dto, ctx);
    }
  });
export type UpdateNutritionLogDto = z.infer<typeof UpdateNutritionLogDtoSchema>;

// ─── Daily totals (read-side aggregate) ───────────────────────────────────

// Per-mealType breakdown used inside DailyTotalsSchema. Same shape as the
// `total` block, but scoped to a single meal slot.
export const MealTotalsBlockSchema = z.object({
  kcal: z.number().min(0),
  proteinG: z.number().min(0),
  carbsG: z.number().min(0),
  fatG: z.number().min(0),
  entryCount: z.number().int().min(0),
});
export type MealTotalsBlock = z.infer<typeof MealTotalsBlockSchema>;

// Response shape for `GET /v1/logs/nutrition/daily-totals?date=YYYY-MM-DD`.
// Computed read-side via SUM over NutritionLog rows scoped to
// [userId, localDate]. See ADR-0013 for why this is preferred over a stored
// daily-aggregate row. The byMealType map carries every meal slot, even
// empty ones, so the dashboard card can render placeholders without
// branching on undefined.
export const DailyTotalsSchema = z.object({
  localDate: LocalDateSchema,
  total: MealTotalsBlockSchema,
  byMealType: z.object({
    breakfast: MealTotalsBlockSchema,
    lunch: MealTotalsBlockSchema,
    dinner: MealTotalsBlockSchema,
    snacks: MealTotalsBlockSchema,
  }),
});
export type DailyTotals = z.infer<typeof DailyTotalsSchema>;

// ─── List response (entries + totals) ─────────────────────────────────────

// Response shape for `GET /v1/logs/nutrition?date=YYYY-MM-DD&timezone=...`.
// Returns the rows themselves alongside the same totals block — saves the
// dashboard card from issuing two requests when it wants both views.
export const ListNutritionLogsQuerySchema = z.object({
  date: LocalDateSchema.optional(),
  timezone: TimezoneSchema,
});
export type ListNutritionLogsQuery = z.infer<typeof ListNutritionLogsQuerySchema>;

export const ListNutritionLogsResponseSchema = z.object({
  entries: z.array(NutritionLogSchema),
  totals: DailyTotalsSchema,
});
export type ListNutritionLogsResponse = z.infer<typeof ListNutritionLogsResponseSchema>;

export const DailyTotalsQuerySchema = ListNutritionLogsQuerySchema;
export type DailyTotalsQuery = z.infer<typeof DailyTotalsQuerySchema>;

// ─── Copy diet day (atomic batch) ─────────────────────────────────────────

// Input for the bulk "clear my logs for one local-calendar day" surface.
// Atomic — every row matching (userId, localDate) is deleted in one
// transaction. Use case: user copied the diet by mistake and wants to
// start the day's logging from scratch.
//
// `timezone`: required. Used to compute localDate when `date` is omitted.
// `date`: optional. When supplied, must be `YYYY-MM-DD`. Otherwise the
//   server uses today in `timezone`.
export const ClearDayDtoSchema = z
  .object({
    timezone: TimezoneSchema,
    date: LocalDateSchema.optional(),
  })
  .strict();
export type ClearDayDto = z.infer<typeof ClearDayDtoSchema>;

export const ClearDayResponseSchema = z.object({
  deletedCount: z.number().int().nonnegative(),
  localDate: LocalDateSchema,
});
export type ClearDayResponse = z.infer<typeof ClearDayResponseSchema>;
